import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
// Import any sub-components if you create them (e.g., DroneOpsAbout)
import DroneOpsAbout from "./DroneOpsAbout"; // Optional - create this if needed

const DroneOpsHome = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "Pilot";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("About DroneOps");
  const [isGroupSelectorOpen, setIsGroupSelectorOpen] = useState(true);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  // ─── Groups / Categories ───
  const groupNames = [
    { label: "About DroneOps", emoji: "🚁" },
    { label: "Drone Operations", emoji: "🛫" },
    { label: "Think5Whys RCA", emoji: "🧠" },
    { label: "Compliance & DGCA", emoji: "📜" },
    { label: "AI & Insights", emoji: "🤖" },
    { label: "Workflows & BPM", emoji: "⚙️" },
    { label: "Fleet & Assets", emoji: "📦" },
    { label: "Training & Skill", emoji: "🎓" },
    { label: "Reports & Dashboards", emoji: "📊" },
    { label: "DeepThink Lab", emoji: "🔬" },
    { label: "Quantum Roadmap", emoji: "⚛️" },
  ];

  // ─── Menu Buttons (grouped) ───
  const menuButtons = [
    // Drone Operations
    { label: "Mission Planner", emoji: "🗺️", link: "/droneops/mission-planner", group: "Drone Operations" },
    { label: "Live Flight Tracker", emoji: "📍", link: "/droneops/live-tracker", group: "Drone Operations" },
    { label: "Pre-Flight Checklist", emoji: "✅", link: "/droneops/checklists", group: "Drone Operations" },
    { label: "Post-Flight Log", emoji: "📝", link: "/droneops/post-flight", group: "Drone Operations" },
    { label: "Sector Templates", emoji: "🌾", link: "/droneops/sector-templates", group: "Drone Operations" },

    // Think5Whys RCA
    { label: "5 Whys Analyzer", emoji: "❓", link: "/fivewhys-analysis-home", group: "Think5Whys RCA" },
    { label: "RCA Templates for Drones", emoji: "🔍", link: "/droneops/rca-templates", group: "Think5Whys RCA" },
    { label: "Anomaly Trigger", emoji: "⚠️", link: "/droneops/anomaly-rca", group: "Think5Whys RCA" },
    { label: "Cause-Effect Map", emoji: "🌳", link: "/droneops/cause-map", group: "Think5Whys RCA" },
    { label: "Mission Failure Review", emoji: "📉", link: "/droneops/mission-review", group: "Think5Whys RCA" },

    // Compliance & DGCA
    { label: "DGCA Digital Sky Sync", emoji: "☁️", link: "/droneops/dgca-sync", group: "Compliance & DGCA" },
    { label: "NPNT & UIN Manager", emoji: "🪪", link: "/droneops/compliance", group: "Compliance & DGCA" },
    { label: "Regulatory Logbook", emoji: "📖", link: "/droneops/reg-log", group: "Compliance & DGCA" },
    { label: "Permission Requests", emoji: "📨", link: "/droneops/permissions", group: "Compliance & DGCA" },

    // AI & Insights
    { label: "GenAI Mission Summary", emoji: "📝", link: "/aicounselling", group: "AI & Insights" },
    { label: "XAI Anomaly Explainer", emoji: "🔎", link: "/droneops/xai-explain", group: "AI & Insights" },
    { label: "Auto RCA Drafts", emoji: "✍️", link: "/droneops/ai-rca", group: "AI & Insights" },
    { label: "Predictive Maintenance", emoji: "🔮", link: "/droneops/predictive", group: "AI & Insights" },

    // Workflows & BPM
    { label: "Process Builder", emoji: "🛠️", link: "/processifyd-processes", group: "Workflows & BPM" },
    { label: "SOP Library", emoji: "📋", link: "/droneops/sop-library", group: "Workflows & BPM" },
    { label: "DMAIC Workflows", emoji: "🔄", link: "/droneops/dmaic", group: "Workflows & BPM" },
    { label: "Kaizen Events", emoji: "🔁", link: "/mykaizen-home", group: "Workflows & BPM" },

    // Fleet & Assets
    { label: "Drone Fleet Dashboard", emoji: "🚁", link: "/droneops/fleet", group: "Fleet & Assets" },
    { label: "Maintenance Scheduler", emoji: "🛠️", link: "/droneops/maintenance", group: "Fleet & Assets" },
    { label: "Battery & Sensor Health", emoji: "🔋", link: "/droneops/asset-health", group: "Fleet & Assets" },

    // Training & Skill
    { label: "Drone Pilot Training", emoji: "🎓", link: "/courses", group: "Training & Skill" },
    { label: "Skill Development Hub", emoji: "🧠", link: "/droneops/skill-dev", group: "Training & Skill" },
    { label: "Simulation Arena", emoji: "🎮", link: "/droneops/simulator", group: "Training & Skill" },

    // Reports & Dashboards
    { label: "Operations Dashboard", emoji: "📊", link: "/droneopsdashboard", group: "Reports & Dashboards" },
    { label: "Compliance Reports", emoji: "📑", link: "/droneops/reports", group: "Reports & Dashboards" },
    { label: "ROI & Efficiency Metrics", emoji: "💰", link: "/droneops/roi", group: "Reports & Dashboards" },

    // DeepThink Lab & Quantum
    { label: "DeepThink Lab", emoji: "🔬", link: "/rcas/add", group: "DeepThink Lab" },
    { label: "Chronicle", emoji: "📖", link: "/tasks/add", group: "DeepThink Lab" },
    { label: "Quantum-Ready Simulator", emoji: "⚛️", link: "/droneops/quantum", group: "Quantum Roadmap" },
  ];

  const filteredButtons = menuButtons.filter((btn) => btn.group === activeTab);
  const selectedGroup = groupNames.find((g) => g.label === activeTab);

  const handleSelectGroup = (label) => {
    setActiveTab(label);
    setIsGroupSelectorOpen(false);
  };

  const toggleGroupSelector = () => {
    setIsGroupSelectorOpen((prev) => !prev);
  };

  return (
    <div className="min-h-screen flex flex-col bg-zinc-950 [&_p]:text-white text-gray-100 font-sans">
      <header className="sticky top-0 z-50 bg-black border-b border-yellow-500/30 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="text-3xl font-extrabold text-yellow-300 tracking-wider">🚁 DroneOps</div>
          <div className="hidden sm:block text-xs text-gray-400">
            Think5Whys DroneOps — Cognitive Process Platform for Drone Excellence
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-yellow-300 text-sm">Welcome, {userName}</span>
              <button
                onClick={handleLogout}
                className="w-9 h-9 flex items-center justify-center bg-red-600 hover:bg-red-700 rounded-full transition text-lg"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="w-20 px-5 py-2 text-sm border border-gray-600 rounded-full hover:bg-zinc-900 transition"
              >
                Login
              </button>
            </>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden text-2xl text-yellow-300 h-12"
          aria-label="Toggle Menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-zinc-900 border-b border-zinc-800 px-6 py-6 text-center">
          {isLoggedIn && (
            <div className="mb-6">
              <p className="text-yellow-300">Welcome, {userName}</p>
              <div className="flex justify-center gap-4 mt-3">
                <button onClick={handleLogout} className="w-12 h-12 bg-red-600 rounded-full text-2xl">⏻</button>
              </div>
            </div>
          )}
          {!isLoggedIn && (
            <div className="flex justify-center gap-4 mb-6">
              <button onClick={() => navigate("/login")} className="px-6 py-2 border border-gray-600 rounded-full">Login</button>
            </div>
          )}
        </div>
      )}

      {/* Group Selector */}
      <div className="border-b border-zinc-800 bg-zinc-900/70 px-2 py-2">
        {isGroupSelectorOpen ? (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-1">
            {groupNames.map((group) => (
              <button
                key={group.label}
                onClick={() => handleSelectGroup(group.label)}
                className={`flex flex-col items-center p-2 rounded-md transition-all text-center ${
                  activeTab === group.label
                    ? "bg-yellow-600/20 ring-2 ring-yellow-400 text-yellow-200"
                    : "bg-zinc-900 hover:bg-zinc-800"
                }`}
              >
                <span className="text-3xl mb-2">{group.emoji}</span>
                <span className="text-sm font-medium">{group.label}</span>
              </button>
            ))}
          </div>
        ) : (
          selectedGroup && (
            <div className="flex items-center justify-between bg-zinc-900 px-5 py-3 rounded-xl">
              <div className="flex items-center gap-4">
                <span className="text-4xl">{selectedGroup.emoji}</span>
                <div>
                  <div className="text-yellow-300 font-semibold">{selectedGroup.label}</div>
                  <div className="text-xs text-gray-400">Select tools for this category</div>
                </div>
              </div>
              <button
                onClick={toggleGroupSelector}
                className="w-32 px-2 py-2 text-sm bg-blue-600 text-white hover:bg-zinc-700 rounded-lg transition"
              >
                {isGroupSelectorOpen ? "Hide" : "Change Category"}
              </button>
            </div>
          )
        )}
      </div>

      {/* Main Content Area */}
      <main className="flex-1 px-2 py-2 w-full">
        {activeTab === "About DroneOps" ? (
          <DroneOpsAbout /> // Or paste your original hero + sections here
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-1">
            {filteredButtons.length === 0 ? (
              <p className="col-span-full text-center text-gray-500 py-4">No tools available in this category yet.</p>
            ) : (
              filteredButtons.map((btn, idx) => (
                <button
                  key={`${btn.label}-${idx}`}
                  onClick={() => navigate(btn.link)}
                  className="group flex flex-col items-center justify-center p-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-yellow-500/30 rounded-2xl transition-all duration-200"
                >
                  <span className="text-xl lg:text-2xl mb-2 group-hover:scale-110 transition">{btn.emoji}</span>
                  <span className="text-xs lg:text-sm font-medium text-center leading-tight">{btn.label}</span>
                </button>
              ))
            )}
          </div>
        )}
      </main>

      {/* Keep your original Hero, Courses, Faculty, Why Us, Vision sections here if you want them always visible, or move them under "About DroneOps" component */}

      {/* Example: You can keep the Hero as a fixed top section before the group selector if you prefer */}
    <div className="text-center text-[10px] mt-8 text-gray-100">
        “Think5Whys for DroneOps — the Six Sigma brain behind every smart drone mission.” — Sanjeev Kumar 
    </div>
      {/* Footer */}
      <footer className="bg-black border-t border-zinc-800 py-10 px-6 text-center md:text-left">
        <div className="mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-sm text-yellow-200">
          <p className="text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</p>
          <div className="flex flex-wrap gap-6 justify-center">
            <Link to="/about-us" className="text-white hover:text-yellow-300 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-yellow-300 transition">Privacy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-yellow-300 transition">Terms</Link>
            <Link to="/contact-us" className="text-white hover:text-yellow-300 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DroneOpsHome;